import { supabase } from '../lib/supabaseClient';
import { ApiErrorConCodigo, esErrorPermanente } from '../lib/apiErrors';
import { setAttendanceUploader } from '../offline/queue';
import { ENLACE_REPORTE_SEGUNDOS } from '../domain/excel/exportReportes';
import { compressImage } from '../lib/compressImage';
import { completarTrabajadores } from './trabajadoresDeMisTiendas';
import type { MarcaConFoto } from '../domain/fotoRepetida';

const BUCKET = 'photos';

/** El marcado ya existe en el servidor: UNIQUE(client_uuid) o UNIQUE(assignment_id, kind).
 *  Para la cola offline es un ÉXITO (el reintento no debe duplicar). */
export class DuplicateAttendanceError extends Error {
  constructor(message = 'Este marcado ya fue registrado.') {
    super(message);
    this.name = 'DuplicateAttendanceError';
  }
}

/** Error de la API conservando el código que lo originó: `code` de PostgREST/Postgres
 *  (p. ej. `42501`) o `statusCode` HTTP del Storage (p. ej. `403`). Sin ese código la
 *  cola offline no puede distinguir "no hay señal" de "el servidor lo rechazó".
 *
 *  La clase y la clasificación viven en `lib/apiErrors.ts` porque la cola de canjes necesita
 *  exactamente la misma decisión; duplicar la lista de códigos la desincronizaría en silencio. */
export class AttendanceApiError extends ApiErrorConCodigo {
  constructor(message: string, code: string | null = null) {
    super(message, code, 'AttendanceApiError');
  }
}

/** true si el servidor rechazó el marcado de forma DEFINITIVA. Un fallo de red (sin
 *  código), un 5xx o un duplicado devuelven false: eso se reintenta (o ya es éxito). */
export function isPermanentAttendanceError(err: unknown): boolean {
  return esErrorPermanente(err);
}

export interface SubmitAttendanceInput {
  clientUuid: string;
  assignmentId: string;
  kind: 'ingreso' | 'salida';
  deviceTs: string;
  lat: number | null;
  lng: number | null;
  accuracy: number | null;
  photoPath: string;
  origin: 'online' | 'offline_sync';
  flags: string[];
  formResponses: { fieldId: string; value: string }[];
}

async function uploadJpeg(path: string, photo: Blob): Promise<string> {
  const { error } = await supabase.storage.from(BUCKET).upload(path, photo, {
    contentType: 'image/jpeg',
    upsert: true, // reintento de la cola offline: mismo path, mismo contenido
  });
  if (error) {
    const code = (error as { statusCode?: string | number }).statusCode;
    throw new AttendanceApiError(error.message, code === undefined ? null : String(code));
  }
  return path;
}

/** Sube la foto sellada del marcado. Devuelve el `photo_path`. */
export async function uploadAttendancePhoto(
  assignmentId: string,
  clientUuid: string,
  photo: Blob,
): Promise<string> {
  return uploadJpeg(`attendance/${assignmentId}/${clientUuid}.jpg`, photo);
}

/** Sube la foto de un campo extra del formulario. Devuelve el path que se guarda
 *  como `value` en `form_responses`. */
export async function uploadFormFieldPhoto(
  clientUuid: string,
  fieldId: string,
  photo: Blob,
): Promise<string> {
  return uploadJpeg(`form-photos/${clientUuid}-${fieldId}.jpg`, photo);
}

/** Inserta la asistencia y sus respuestas de formulario.
 *
 *  `flags` se envía tal como lo calculó `computeFlags` en el móvil, pero NO es la fuente
 *  de verdad: el trigger `before insert on attendance` (Grupo A) los recalcula en el
 *  servidor con `positions.date/start_time`, `pdv.lat/lng/radius_m` y `app_settings`, y
 *  sobrescribe la columna. Lo del cliente es solo feedback visual inmediato para el
 *  recurso (spec §5: la lógica sensible vive en el servidor). */
export async function submitAttendance(input: SubmitAttendanceInput): Promise<{ id: string }> {
  const { data, error } = await supabase
    .from('attendance')
    .insert({
      assignment_id: input.assignmentId,
      kind: input.kind,
      device_ts: input.deviceTs,
      lat: input.lat,
      lng: input.lng,
      accuracy: input.accuracy,
      photo_path: input.photoPath,
      origin: input.origin,
      flags: input.flags,
      client_uuid: input.clientUuid,
    })
    .select('id')
    .single();

  if (error) {
    if (error.code === '23505') throw new DuplicateAttendanceError();
    throw new AttendanceApiError(error.message, error.code ?? null);
  }

  const attendanceId = data.id as string;

  if (input.formResponses.length > 0) {
    const { error: formError } = await supabase.from('form_responses').insert(
      input.formResponses.map((r) => ({
        attendance_id: attendanceId,
        field_id: r.fieldId,
        value: r.value,
      })),
    );
    // 23505 aquí = UNIQUE(attendance_id, field_id): reintento, ya estaban guardadas.
    if (formError && formError.code !== '23505') {
      throw new AttendanceApiError(formError.message, formError.code ?? null);
    }
  }

  return { id: attendanceId };
}

export interface MyAttendanceRow {
  id: string;
  assignment_id: string;
  kind: 'ingreso' | 'salida';
  device_ts: string;
  flags: string[];
  status: 'pendiente' | 'validada' | 'observada';
  /** Ruta dentro del bucket privado `photos`. Se convierte en imagen visible con
   *  `getPhotoUrl` (URL firmada de 60 s). Puede faltar en marcados muy viejos. */
  photo_path: string | null;
  assignment: {
    position: {
      date: string;
      start_time: string;
      end_time: string;
      pdv: { name: string } | null;
      campaign: { client: string; brand: string } | null;
    };
  };
}

const MY_ATTENDANCE_SELECT = `id, assignment_id, kind, device_ts, flags, status, photo_path,
  assignment:assignments!inner(
    position:positions!inner(date, start_time, end_time,
      pdv:pdv(name), campaign:campaigns(client, brand)))`;

/**
 * Últimas 30 asistencias PROPIAS desde `dateFrom` (YYYY-MM-DD, fecha de la posición).
 *
 * Filtra por `worker_id` de forma explícita, y no se apoya en la RLS como antes. La RLS limita
 * bien a un recurso puro, pero a quien además tiene un rol de oficina —admin, coordinador,
 * supervisor— le deja ver a TODO el mundo: la pantalla "Mi historial" le mostraba los marcados
 * de toda la campaña bajo un título que dice "mi". Lo notó el usuario, que es admin.
 *
 * La regla que deja: **una pantalla que dice "mío" filtra por el usuario, no confía en que los
 * permisos lo hagan.** Los permisos protegen de quien no debe ver; no son quien decide qué
 * significa "mío".
 */
export async function listMyAttendance(dateFrom: string): Promise<MyAttendanceRow[]> {
  const { data: sesion } = await supabase.auth.getUser();
  const yo = sesion?.user?.id;
  // Sin sesión no hay "mi historial" que mostrar. Devolver vacío es más honesto que devolver
  // lo que la RLS deje pasar.
  if (!yo) return [];
  // Dos pasos y NO un filtro por la tabla embebida. Antes era attendance con `.eq('assignment.worker_id')` y
  // `.gte('assignment.position.date')`: en PostgREST eso no recorta la tabla base, así que la base recorría TODA la
  // asistencia (con RLS) en cada «Mi turno» y se cortaba a los 8 s. El 19-oct eran 325 de los 338 errores 500 del día
  // y 1,5 s de promedio en las que sí respondían. Se parte de la tabla que tiene la columna (CLAUDE.md): mis
  // asignaciones desde la fecha (assignments.worker_id con índice, son pocas) y después sus marcas
  // (attendance.assignment_id con índice).
  const { data: mias, error: errA } = await supabase
    .from('assignments')
    .select('id, position:positions!inner(date)')
    .eq('worker_id', yo)
    .gte('position.date', dateFrom);
  if (errA) throw new AttendanceApiError(errA.message, errA.code ?? null);
  const ids = ((mias ?? []) as { id: string }[]).map((a) => a.id);
  if (ids.length === 0) return [];
  const { data, error } = await supabase
    .from('attendance')
    .select(MY_ATTENDANCE_SELECT)
    .in('assignment_id', ids)
    .order('device_ts', { ascending: false })
    .limit(30);
  if (error) throw new AttendanceApiError(error.message, error.code ?? null);
  return (data ?? []) as unknown as MyAttendanceRow[];
}

/**
 * Hora (`device_ts`) del INGRESO ya marcado de esa asignación, o null si todavía no hay.
 * La usa la pantalla de marcado para avisar, ANTES de registrar, que la salida va a
 * quedar como "Salida antes de tiempo" (regla del medio día). `attendance` tiene
 * UNIQUE(assignment_id, kind), así que como mucho hay una fila; la RLS limita la
 * lectura al propio recurso. Si falla —sin señal, por ejemplo— devuelve null: no
 * poder avisar nunca debe impedir marcar la salida.
 */
export async function getEntryDeviceTs(assignmentId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('attendance')
    .select('device_ts')
    .eq('assignment_id', assignmentId)
    .eq('kind', 'ingreso')
    .limit(1)
    .maybeSingle();
  if (error) throw new AttendanceApiError(error.message, error.code ?? null);
  return (data as { device_ts: string } | null)?.device_ts ?? null;
}

// --- Validación (Task 30) ---

/**
 * PostgREST corta cada respuesta en el parámetro "Max rows" del proyecto (1000 por
 * defecto) SIN avisar. Un cierre mensual de una campaña con 40 recursos ya son ~2.000
 * filas de `attendance`: leer sin paginar haría validar (y pagar) de menos.
 * Por eso esta lectura pagina con `.range()` y compara contra el `count: 'exact'`.
 */
const PAGE_SIZE = 1000;

export interface ValidationRow {
  id: string;
  kind: 'ingreso' | 'salida';
  device_ts: string;
  /**
   * De dónde salió la marca. `manual` (0076) la escribió un coordinador o supervisor: la pantalla
   * la pinta con su propia etiqueta porque no es lo mismo que una marca del campo.
   * `garra` (0263) se marcó en la app GARRA y la copió de noche la función `garra-asistencia`.
   */
  origin: 'online' | 'offline_sync' | 'carga_historica' | 'manual' | 'garra';
  flags: string[];
  status: 'pendiente' | 'validada' | 'observada';
  lat: number | null;
  lng: number | null;
  photo_path: string;
  /**
   * Huella de la foto (eTag de Storage, 0314). Con ella se buscan las otras marcas que llevan la
   * misma foto cuando la fila trae la bandera `foto_repetida`. Null si la foto no está en el bucket.
   */
  foto_huella: string | null;
  validation_note: string | null;
  assignment: {
    id: string;
    /** Para completar el nombre cuando el supervisor no lee `profiles` (0218). */
    worker_id?: string;
    worker: { id: string; dni: string; first_names: string; last_names: string };
    position: {
      date: string; start_time: string; end_time: string;
      /** Para filtrar por supervisor: el reparto es por tienda (`campaign_pdv_supervisors`). */
      pdv_id?: string;
      pdv: { name: string } | null;
    };
  };
}

/**
 * Cuántas asignaciones van en cada pedido de marcas. 150 uuid son ~5 600 caracteres de URL, lejos
 * del límite, y cada lote trae a lo más 300 marcas (ingreso y salida), por debajo de `PAGE_SIZE`.
 */
const LOTE_ASIGNACIONES = 150;

/**
 * Las marcas de una campaña en un rango, para Validación.
 *
 * 🔴 ARRANCA POR LOS TURNOS, NO POR LAS MARCAS. Hasta el 29-set la consulta salía de `attendance` y
 * filtraba campaña y fecha sobre los embebidos (`assignment.position.campaign_id`). PostgREST no puede
 * usar esos filtros para recortar la tabla base, así que la base evaluaba los permisos (RLS) de TODAS
 * las marcas del sistema —~1 900 al 29-set, y creciendo cada día— para devolver las 28 de una
 * campaña. Promedio 1,5 s, máximo 7,2 s, y ese día se cortó por el límite de 8 s: Validación
 * mostraba un error en vez de las marcas y las faltas. Ahora se piden primero los turnos (filtro
 * directo sobre `positions`) y después solo las marcas de esas asignaciones (`assignment_id` en lote),
 * que es un filtro sobre la tabla base con índice.
 */
export async function listAttendanceForValidation(
  campaignId: string, dateFrom: string, dateTo: string,
): Promise<ValidationRow[]> {
  // 1) Las asignaciones de los turnos del rango. Todas, sin mirar el estado: una marca cuenta aunque
  //    su asignación después se haya reemplazado, igual que en la consulta de antes.
  const asignaciones: string[] = [];
  let totalTurnos: number | null = null;
  let turnosLeidos = 0;
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error, count } = await supabase
      .from('positions')
      .select('id, assignments(id)', { count: 'exact' })
      .eq('campaign_id', campaignId)
      .gte('date', dateFrom)
      .lte('date', dateTo)
      .order('date')
      .order('id') // desempate estable: sin él, dos páginas podrían repetir u omitir filas
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    if (count !== null) totalTurnos = count;
    const batch = (data ?? []) as { id: string; assignments: { id: string }[] | null }[];
    turnosLeidos += batch.length;
    for (const p of batch) for (const a of p.assignments ?? []) asignaciones.push(a.id);
    if (batch.length < PAGE_SIZE) break;
  }
  if (totalTurnos !== null && turnosLeidos !== totalTurnos) {
    throw new Error(
      `Lectura incompleta de turnos: se recibieron ${turnosLeidos} de ${totalTurnos}. ` +
      'No valides con estos datos: reduce el rango de fechas y vuelve a intentar.',
    );
  }

  // 2) Las marcas de esas asignaciones, por lotes.
  const rows: ValidationRow[] = [];
  for (let i = 0; i < asignaciones.length; i += LOTE_ASIGNACIONES) {
    const lote = asignaciones.slice(i, i + LOTE_ASIGNACIONES);
    // `profiles!assignments_worker_id_fkey`: `assignments` tiene TRES FK a `profiles`
    // (worker_id, assigned_by, approved_by); sin el hint PostgREST responde error 300.
    const { data, error, count } = await supabase
      .from('attendance')
      .select(
        `id, kind, device_ts, origin, flags, status, lat, lng, photo_path, foto_huella, validation_note,
        assignment:assignments!inner(id, worker_id,
          worker:profiles!assignments_worker_id_fkey(id, dni, first_names, last_names),
          position:positions!inner(date, start_time, end_time, campaign_id, pdv_id, pdv:pdv(name)))`,
        { count: 'exact' },
      )
      .in('assignment_id', lote)
      .range(0, PAGE_SIZE - 1);
    if (error) throw error;
    const batch = (data ?? []) as unknown as ValidationRow[];
    if (count !== null && batch.length !== count) {
      throw new Error(
        `Lectura incompleta de asistencias: se recibieron ${batch.length} de ${count} filas. ` +
        'No valides con estos datos: reduce el rango de fechas y vuelve a intentar.',
      );
    }
    rows.push(...batch);
  }
  // La más reciente primero, con desempate estable: el mismo orden que daba la consulta de antes.
  rows.sort((a, b) => (a.device_ts < b.device_ts ? 1 : a.device_ts > b.device_ts ? -1
    : a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  // El supervisor corrige marcaciones de sus tiendas (0228) pero no lee `profiles`: su `worker`
  // llega en null y sin esto la pantalla se caía en el primer `worker.id`.
  await completarTrabajadores(rows.map((r) => r.assignment));
  return rows;
}

/** Huellas por pedido: 100 md5 son ~3 400 caracteres de URL, lejos del límite. */
const LOTE_HUELLAS = 100;

/**
 * Todas las marcas que llevan alguna de estas fotos (por huella), para decir en Validación con
 * cuál comparte la foto una marca con la bandera `foto_repetida`.
 *
 * Va con los permisos de quien pregunta, a propósito: el coordinador ve las de sus campañas y el
 * supervisor las de sus tiendas. Lo que no le toca no llega, y la pantalla dice que la otra marca
 * no está a su vista en vez de nombrarla. El filtro es sobre la tabla base con índice
 * (`attendance_foto_huella_idx`), así que la RLS se evalúa solo en las filas que coinciden.
 */
export async function listMarcasConLaMismaFoto(huellas: readonly string[]): Promise<MarcaConFoto[]> {
  const unicas = [...new Set(huellas.filter((h) => h !== ''))];
  const out: MarcaConFoto[] = [];
  for (let i = 0; i < unicas.length; i += LOTE_HUELLAS) {
    const lote = unicas.slice(i, i + LOTE_HUELLAS);
    const { data, error } = await supabase
      .from('attendance')
      .select(
        `id, foto_huella, kind,
        assignment:assignments!inner(id, worker_id,
          worker:profiles!assignments_worker_id_fkey(first_names, last_names),
          position:positions(date, pdv:pdv(name)))`,
      )
      .in('foto_huella', lote);
    if (error) throw error;
    out.push(...((data ?? []) as unknown as MarcaConFoto[]));
  }
  await completarTrabajadores(out.map((r) => r.assignment));
  return out;
}

/** URL firmada de 60 segundos para una foto del bucket privado `photos`. */
export async function getPhotoUrl(path: string): Promise<string> {
  // La evidencia de una CARGA HISTORICA no vive en el Storage: la planilla de agosto traia las
  // fotos como enlaces de Google Drive (0045). Firmarlas como si fueran una ruta del bucket
  // devuelve un error y la foto se ve como "no disponible" — la evidencia existe y no se puede
  // mirar, que es el peor de los dos mundos. Si ya es una URL, se abre tal cual.
  if (/^https?:\/\//i.test(path)) return path;
  const { data, error } = await supabase.storage.from('photos').createSignedUrl(path, 60);
  if (error || !data) throw error ?? new Error('No se pudo firmar la URL de la foto');
  return data.signedUrl;
}

/** Se firma de a tandas: el endpoint recibe una lista, no miles de rutas de una vez. */
const TANDA_FIRMA = 100;

/**
 * Firma muchas fotos de una sola vez, con vencimiento LARGO, para pegarlas en un reporte.
 *
 * **Estos enlaces abren sin iniciar sesión.** Es una decisión explícita del jefe de operaciones
 * (2026-08-18): el reporte tiene que poder mandarse a alguien que no tiene usuario del sistema.
 * El token viaja dentro de la URL, así que quien reciba el archivo ve esas fotos — y no se puede
 * anular un enlace suelto sin mover o borrar el objeto.
 *
 * Lo que NO se hizo, y no debe hacerse: abrir el bucket. `photos` guarda también `documents/` y
 * `applications/`, o sea las fotos de DNI de trabajadores y postulantes. Hacerlo público
 * publicaría eso, que es justo la práctica que se descartó del prototipo anterior. Firmando por
 * ruta se expone solo lo que ese reporte incluye.
 *
 * Devuelve un mapa ruta → URL. Las que no se pudieron firmar simplemente no están: el reporte
 * deja esa celda vacía en vez de escribir un enlace roto.
 */
export async function firmarFotosParaReporte(
  paths: string[], expiraEn: number = ENLACE_REPORTE_SEGUNDOS,
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  // Las de la carga histórica ya son URL absolutas de Drive (0045): no hay nada que firmar.
  const delBucket: string[] = [];
  for (const p of new Set(paths)) {
    if (p === '') continue;
    if (/^https?:\/\//i.test(p)) out.set(p, p); else delBucket.push(p);
  }

  for (let i = 0; i < delBucket.length; i += TANDA_FIRMA) {
    const tanda = delBucket.slice(i, i + TANDA_FIRMA);
    const { data, error } = await supabase.storage
      .from('photos')
      .createSignedUrls(tanda, expiraEn);
    // Un fallo de una tanda no cancela el reporte: se pierden esos enlaces, no el archivo.
    if (error || !data) continue;
    for (const fila of data) {
      if (fila.path && fila.signedUrl && !fila.error) out.set(fila.path, fila.signedUrl);
    }
  }
  return out;
}

/** Lado mayor de la miniatura que se incrusta en el HTML. A 900 px una boleta se lee. */
const MINIATURA_LADO = 900;

/**
 * Baja las fotos y las devuelve como `data:` URI, para incrustarlas en un reporte HTML.
 *
 * Se reducen a miniatura antes de incrustar: en base64 cada imagen crece ~33%, y a tamaño
 * original un reporte de un mes pasa de los 30 MB — un archivo que nadie manda por correo. A
 * 900 px del lado mayor una boleta sigue siendo legible.
 *
 * Una foto que falla NO cancela el reporte: se omite del mapa y el HTML escribe "no se pudo
 * incluir esta foto" en su lugar. Perder una foto es un problema; perder el reporte entero por
 * una foto es peor.
 */
export async function descargarFotosEmbebidas(
  paths: string[], onProgress?: (hechas: number, total: number) => void,
): Promise<Map<string, string>> {
  const unicas = [...new Set(paths.filter((p) => p.trim() !== ''))];
  const out = new Map<string, string>();
  let hechas = 0;
  for (const path of unicas) {
    try {
      const url = await getPhotoUrl(path);
      const blob = await fetch(url).then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.blob();
      });
      const chica = await compressImage(blob, MINIATURA_LADO, 0.72);
      out.set(path, await new Promise<string>((resolve, reject) => {
        const lector = new FileReader();
        lector.onload = () => resolve(String(lector.result));
        lector.onerror = () => reject(lector.error ?? new Error('No se pudo leer la foto'));
        lector.readAsDataURL(chica);
      }));
    } catch {
      // se omite: el reporte lo dice en su lugar
    }
    hechas += 1;
    onProgress?.(hechas, unicas.length);
  }
  return out;
}

/**
 * Cambia el estado de validación en lote. `pendiente` REABRE una asistencia ya
 * validada u observada (la RPC exige nota y lo audita): un clic errado en
 * "Observar (250)" no puede dejar esas asistencias fuera del pago para siempre.
 */
export async function validateAttendance(
  ids: string[], newStatus: 'validada' | 'observada' | 'pendiente', note: string,
): Promise<number> {
  const { data, error } = await supabase.rpc('validate_attendance', {
    ids, new_status: newStatus, note,
  });
  if (error) throw error;
  return data as number;
}

/**
 * Registra la SALIDA que el recurso no marcó (spec §6 "Sin salida": horas no
 * calculables → validación manual del coordinador con hora justificada y auditada).
 * `deviceTs` es ISO con el offset fijo de Lima (`-05:00`, Perú no tiene horario de
 * verano). La RPC `set_manual_exit` valida el rol, inserta la asistencia `salida`
 * con `origin = 'offline_sync'` y `status = 'validada'`, guarda el motivo en
 * `validation_note` y escribe `audit_log`.
 */
export async function setManualExit(
  assignmentId: string, deviceTs: string, note: string,
): Promise<void> {
  const { error } = await supabase.rpc('set_manual_exit', {
    p_assignment: assignmentId, p_device_ts: deviceTs, p_note: note,
  });
  if (error) throw error;
}

/**
 * Los campos de una marca que `corregir_asistencia` (0074) acepta. La lista vive acá y en la RPC:
 * si crecen distinto, el servidor manda —rechaza con el nombre del campo—.
 *
 * NO están fecha, PDV ni trabajador, y no es un olvido: los tres salen de `assignment_id`, así que
 * cambiarlos sería mover la marca a otra asignación. Ver el bloque 1 de la 0074.
 */
export type CampoCorregible = 'device_ts' | 'photo_path' | 'validation_note' | 'kind';

/**
 * Sube la foto con la que se REEMPLAZA la de una marca ya registrada, y devuelve su ruta.
 *
 * **Por qué un nombre nuevo y no el del marcado.** `uploadAttendancePhoto` sube a
 * `attendance/<assignment>/<client_uuid>.jpg` con `upsert: true`, que es lo correcto para el
 * celular —reintentar la misma foto no debe fallar— pero acá pisaría la foto que tomó el recurso, y
 * la evidencia original desaparecería del bucket aunque `attendance_corrections` siguiera guardando
 * su ruta. Con un nombre nuevo por corrección no se sobrescribe nada: la vieja queda donde estaba y
 * el rastro la sigue apuntando, así que se puede volver a mirar lo que el campo reportó de verdad.
 *
 * La vieja tampoco se borra a propósito: Supabase no borra del Storage por SQL, y borrarla
 * convertiría el historial en un puntero a nada.
 *
 * Se queda bajo `attendance/` porque es el único prefijo que la RPC acepta desde la 0075 —
 * justamente para que nadie pueda apuntar esta columna a `documents/`, donde viven los DNI.
 */
export function subirFotoDeCorreccionAsistencia(
  assignmentId: string, foto: Blob,
): Promise<string> {
  return uploadJpeg(`attendance/${assignmentId}/correccion-${crypto.randomUUID()}.jpg`, foto);
}

/**
 * Corrige una marca de asistencia. `cambios` lleva SOLO los campos que de verdad cambian: la RPC
 * escribe una fila de rastro por cada clave que reciba, así que mandar un campo con su mismo valor
 * ensuciaría el historial con una corrección que no corrigió nada.
 *
 * El motivo es obligatorio del lado del servidor; acá se valida antes solo para no gastar el viaje.
 */
export async function corregirAsistencia(
  recordId: string, cambios: Partial<Record<CampoCorregible, string>>, motivo: string,
): Promise<void> {
  const { error } = await supabase.rpc('corregir_asistencia', {
    p_record: recordId, p_cambios: cambios, p_motivo: motivo,
  });
  if (error) throw error;
}

/**
 * Sube la foto de un registro hecho a mano y devuelve su ruta. Mismo destino y mismas razones que
 * `subirFotoDeCorreccionAsistencia`: nombre nuevo cada vez, para no pisar nunca una foto del campo.
 */
export function subirFotoDeAsistenciaManual(
  assignmentId: string, foto: Blob,
): Promise<string> {
  return uploadJpeg(`attendance/${assignmentId}/manual-${crypto.randomUUID()}.jpg`, foto);
}

/**
 * Crea el ingreso o la salida que el campo nunca marcó, sobre una asignación que ya existe.
 *
 * La fecha, el PDV y el trabajador NO viajan: salen de la asignación. `deviceTs` es ISO con el
 * offset fijo de Lima. La foto y el motivo son obligatorios del lado del servidor —la foto es la
 * evidencia del registro— y la RPC rechaza duplicados: si esa marca ya existe, lo que corresponde
 * es corregirla, no crear otra.
 *
 * Queda con `origin = 'manual'` y `status = 'validada'`, siguiendo el precedente de
 * `set_manual_exit`: quien la escribe es el coordinador, o sea el mismo que valida.
 */
export async function registrarAsistenciaManual(
  assignmentId: string,
  kind: 'ingreso' | 'salida',
  deviceTs: string,
  photoPath: string,
  nota: string,
): Promise<string> {
  const { data, error } = await supabase.rpc('registrar_asistencia_manual', {
    p_assignment: assignmentId,
    p_kind: kind,
    p_device_ts: deviceTs,
    p_photo_path: photoPath,
    p_nota: nota,
  });
  if (error) throw error;
  return data as string;
}

/**
 * Borra una marca de asistencia de verdad, sin dejarla a la vista en ningún lado.
 *
 * **Por qué borrado real y no anulada.** Pedido del usuario (2026-09-09): cuando el recurso marca
 * mal no es un dato con matices, es un error total, y un registro tachado en pantalla sigue
 * invitando a preguntar qué pasó. Lo que queda después del borrado es la asignación: el turno se
 * lee como falta y el recurso puede volver a marcar, que es justamente lo que el UNIQUE
 * `attendance_una_marca_por_tipo` le impedía mientras la marca equivocada existiera.
 *
 * **Rastro.** Que no se vea no quiere decir que no quede. Un disparador `before delete` copia la
 * fila entera al `audit_log` antes de que desaparezca, y la RPC guarda el motivo aparte. Adentro
 * se puede reconstruir qué decía la marca, quién la borró y por qué; afuera no queda nada.
 *
 * La foto NO se borra del bucket a propósito: es la evidencia de que la marca existió, y su ruta
 * queda anotada en el rastro. Borrarla dejaría el historial apuntando a la nada.
 *
 * El servidor exige `admin` y devuelve el rechazo en el cuerpo, no como error de Postgres: por eso
 * se revisa `error` antes de dar el borrado por hecho.
 */
export async function borrarAsistencia(recordId: string, motivo: string): Promise<void> {
  const { data, error } = await supabase.rpc('borrar_asistencia', {
    p_id: recordId, p_motivo: motivo,
  });
  if (error) throw error;
  const r = (data ?? {}) as { ok?: boolean; error?: string };
  if (r.error) throw new Error(r.error);
  if (!r.ok) throw new Error('El servidor no confirmó el borrado.');
}

/** Una línea del rastro de correcciones, como la muestra el historial de la marca. */
export interface CorreccionAsistenciaRow {
  campo: string;
  valor_anterior: string | null;
  valor_nuevo: string | null;
  motivo: string;
  created_at: string;
}

/**
 * Cuántas correcciones tiene cada marca del lote. Alimenta la etiqueta «Editado» de la tabla.
 *
 * Se pregunta por el lote y no fila por fila porque la pantalla trae hasta 500 marcas: una consulta
 * por fila serían 500 viajes para pintar una etiqueta.
 */
export async function contarCorreccionesAsistencia(
  recordIds: string[],
): Promise<Map<string, number>> {
  const out = new Map<string, number>();
  if (recordIds.length === 0) return out;
  const { data, error } = await supabase
    .from('attendance_corrections')
    .select('record_id')
    .in('record_id', recordIds);
  if (error) throw error;
  for (const row of (data ?? []) as { record_id: string }[]) {
    out.set(row.record_id, (out.get(row.record_id) ?? 0) + 1);
  }
  return out;
}

/** El rastro completo de UNA marca, del cambio más nuevo al más viejo. */
export async function listCorreccionesAsistencia(
  recordId: string,
): Promise<CorreccionAsistenciaRow[]> {
  const { data, error } = await supabase
    .from('attendance_corrections')
    .select('campo, valor_anterior, valor_nuevo, motivo, created_at')
    .eq('record_id', recordId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as CorreccionAsistenciaRow[];
}

// Registra este módulo como enviador de la cola offline (Task 25). Importar
// `api/attendance` en cualquier punto de la app deja el flush() operativo.
setAttendanceUploader({
  uploadAttendancePhoto,
  uploadFormFieldPhoto,
  submitAttendance,
  isDuplicateError: (err: unknown) => err instanceof DuplicateAttendanceError,
  isPermanentError: isPermanentAttendanceError,
});
